const WORD_PATTERN = /[\p{L}\p{N}]+(?:['’][\p{L}\p{N}]+)*/gu;

export const MAX_LOCK_WORDS = 8;
export const MAX_LOCK_PHRASES = 5;

export const answerWords = (text = '') => {
  const words = [];
  for (const match of String(text).matchAll(WORD_PATTERN)) {
    words.push({
      text: match[0],
      index: words.length,
      start: match.index,
      end: match.index + match[0].length,
    });
  }
  return words;
};

export const updateWordSelection = (selection = [], tappedIndex, maxWords = MAX_LOCK_WORDS) => {
  if (!Number.isInteger(tappedIndex) || tappedIndex < 0) return selection;
  if (!selection.length || selection.includes(tappedIndex)) return [tappedIndex];

  const sorted = [...selection].sort((a, b) => a - b);
  const first = sorted[0];
  const last = sorted[sorted.length - 1];
  if (tappedIndex !== first - 1 && tappedIndex !== last + 1) return [tappedIndex];

  const next = [...sorted, tappedIndex].sort((a, b) => a - b);
  return next.length > maxWords ? sorted : next;
};

export const phraseFromSelection = (text, selection = []) => {
  if (!selection.length) return '';
  const words = answerWords(text);
  const sorted = [...selection].sort((a, b) => a - b);
  const first = words[sorted[0]];
  const last = words[sorted[sorted.length - 1]];
  if (!first || !last) return '';
  return String(text).slice(first.start, last.end).trim();
};

export const addLockedPhrase = (
  phrases = [],
  phrase,
  maxPhrases = MAX_LOCK_PHRASES,
) => {
  const clean = String(phrase || '').trim().replace(/\s+/g, ' ');
  if (!clean || clean.split(/\s+/).length > MAX_LOCK_WORDS) return phrases;
  if (phrases.length >= maxPhrases) return phrases;
  if (phrases.some((item) => item.toLocaleLowerCase() === clean.toLocaleLowerCase())) {
    return phrases;
  }
  return [...phrases, clean];
};

const normalizedTokens = (text) => (
  (String(text).toLocaleLowerCase().match(WORD_PATTERN) || []).map((word) => word.toLocaleLowerCase())
);

export const highlightLockedPhrases = (line, phrases = []) => {
  const source = String(line || '');
  const words = answerWords(source);
  const normalizedWords = words.map((word) => word.text.toLocaleLowerCase());
  const ranges = [];

  phrases.forEach((phrase) => {
    const target = normalizedTokens(phrase);
    if (!target.length) return;
    for (let index = 0; index <= normalizedWords.length - target.length; index += 1) {
      const candidate = normalizedWords.slice(index, index + target.length);
      if (candidate.every((word, offset) => word === target[offset])) {
        ranges.push({ start: words[index].start, end: words[index + target.length - 1].end });
        index += target.length - 1;
      }
    }
  });

  const merged = ranges
    .sort((a, b) => a.start - b.start || b.end - a.end)
    .reduce((result, range) => {
      const previous = result[result.length - 1];
      if (previous && range.start <= previous.end) previous.end = Math.max(previous.end, range.end);
      else result.push({ ...range });
      return result;
    }, []);

  if (!merged.length) return [{ text: source, highlighted: false }];
  const segments = [];
  let cursor = 0;
  merged.forEach((range) => {
    if (range.start > cursor) segments.push({ text: source.slice(cursor, range.start), highlighted: false });
    segments.push({ text: source.slice(range.start, range.end), highlighted: true });
    cursor = range.end;
  });
  if (cursor < source.length) segments.push({ text: source.slice(cursor), highlighted: false });
  return segments;
};

const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

export const rewriteName = (text, name, replacement) => {
  if (!name) return String(text || '');
  const pattern = new RegExp(`(^|[^\\p{L}\\p{N}])(${escapeRegExp(name)})(?=$|[^\\p{L}\\p{N}])`, 'giu');
  return String(text || '').replace(pattern, (_match, prefix) => `${prefix}${replacement}`);
};

export const calculateResumeStep = (questions = [], answers = []) => {
  const completed = new Set(
    answers
      .filter((answer) => answer.skipped || String(answer.answer_text || answer.text || '').trim())
      .map((answer) => answer.question_id),
  );
  const index = questions.findIndex((question) => !completed.has(question.id));
  return index === -1 ? questions.length : index;
};

export const calculateResumeDestination = (questions = [], answers = [], draft = null) => {
  if (draft) return { step: 'song', questionIndex: null };
  const questionIndex = calculateResumeStep(questions, answers);
  if (questionIndex >= questions.length) return { step: 'name-check', questionIndex: null };
  return { step: 'questions', questionIndex };
};

export const shouldShowCareMessage = (shownQuestionIds = [], questionId, careMessage = false) => (
  Boolean(careMessage && questionId && !shownQuestionIds.includes(questionId))
);

const DEFAULT_MESSAGES = {
  400: 'Check what you entered and try again.',
  401: 'That did not match. Try again.',
  403: 'This part is not ready yet. Ask your teacher for help.',
  404: 'We could not find that.',
  409: 'That choice was just taken. Pick another one.',
  429: 'Too many tries at once. Wait a moment, then try again.',
  503: 'Pizzul needs a minute. Your work is saved, so try again soon.',
};

export const apiErrorMessage = (status, detail = '', context = '') => {
  const message = String(detail || '');
  if (context === 'join') {
    if (status === 404) return 'Check the code with your teacher.';
    if (status === 403) return 'This class is not live yet. Ask your teacher.';
    if (status === 429) return 'Too many code checks at once. Wait a few minutes, then try again.';
  }
  if (context === 'sign-in') {
    if (status === 401) return 'That PIN did not match. Try it again.';
    if (status === 429) return 'Please wait a moment before trying your PIN again.';
    if (status === 403 && /waiting/i.test(message)) return 'Waiting for your teacher to approve you.';
    if (status === 403 && /wrong pins|reset/i.test(message)) return 'Your PIN is locked. Ask your teacher to reset it.';
  }
  if (context === 'lyrics') {
    if (status === 400) return message || 'Answer at least two questions before Pizzul writes.';
    if (status === 429) return message || 'You reached the draft limit. Try again a little later.';
    if (status === 503) return 'Pizzul needs a minute. Your answers are saved, so try again.';
  }
  return message || DEFAULT_MESSAGES[status] || 'Something went wrong. Try again.';
};

// "Take it to the booth": the copy-ready song sheet staff paste into a song tool.

export const songSheetLyrics = (draft = {}) => (draft.sections || [])
  .map((section) => [`[${section.label}]`, ...(section.lines || []).map((line) => line.text)].join('\n'))
  .join('\n\n');

export const cadenceText = (draft = {}) => (draft.cadence || [])
  .map((item) => `${item.label}: ${item.note}`)
  .join('\n');

export const fullSongSheet = (draft = {}) => {
  const parts = [`Title: ${draft.title || 'Untitled'}`];
  if (draft.style) parts.push(`Style: ${draft.style}`);
  const cadence = cadenceText(draft);
  if (cadence) parts.push(`Cadence:\n${cadence}`);
  parts.push(`Lyrics:\n${songSheetLyrics(draft)}`);
  return parts.join('\n\n');
};

// Tempo out of a style line like "boom-bap hip-hop, around 90 BPM". Null when absent.
export const bpmFromStyle = (style = '') => {
  const match = /(\d{2,3})\s*bpm/i.exec(style || '');
  if (!match) return null;
  const bpm = Number(match[1]);
  return bpm >= 50 && bpm <= 200 ? bpm : null;
};

// Style line as chips, without the tempo piece (shown big on its own) or "clean lyrics".
export const styleChips = (style = '') => (style || '')
  .split(/[,;]/)
  .map((part) => part.trim())
  .filter((part) => part && !/\bbpm\b/i.test(part) && !/^clean lyrics?$/i.test(part));
